import { useMemo, useState, useCallback } from 'react';
import {
    AlignItems,
    Container,
    ContainerSize,
    Gap,
    Heading,
    Icon,
    Stack,
    StackDirection,
    Text,
    Wrapper,
} from '@rebootui';
import CodeSnippet from '../../../components/controls/CodeSnippet';
import CodePreview from '../../../components/controls/CodePreview';
import CodeHighlighter from '../../../components/controls/CodeHighlighter';
import { ICON_NAMES } from '../../../data/iconNames';

const basicUsageCode = `import { Icon } from '@reactufy/rebootui';

<Icon name="heart" width={24} />`;

const styledUsageCode = `import { Icon } from '@reactufy/rebootui';

<Icon name="sun" width={28} className="text-warning" />
<Icon name="moon" width={28} className="text-primary" fill="#6f42c1" />`;

const IconsPage = () => {
    const [query, setQuery] = useState('');
    const [copied, setCopied] = useState<string | null>(null);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return [...ICON_NAMES];
        return ICON_NAMES.filter((id) => id.toLowerCase().includes(q));
    }, [query]);

    const copySnippet = useCallback((name: string) => {
        const snippet = `<Icon name="${name}" width={24} />`;
        void navigator.clipboard.writeText(snippet).then(() => {
            setCopied(name);
            window.setTimeout(() => setCopied(null), 2000);
        });
    }, []);

    return (
        <Container size={ContainerSize.Fluid}>
            <Heading el="h1" className="fw-semibold mb-3 ls-1">
                Icons
            </Heading>
            <Text className="mb-4">
                RebootUI ships an SVG sprite (sourced from{' '}
                <Wrapper
                    el="a"
                    href="https://icons.getbootstrap.com/"
                    className="link-underline link-underline-opacity-0"
                    target="_blank"
                    rel="noreferrer"
                >
                    Bootstrap Icons
                </Wrapper>
                ) and an <Text el="code">Icon</Text> component that references each symbol by{' '}
                <Text el="code">name</Text> (the symbol <Text el="code">id</Text> in the sprite).{' '}
                The <Text el="code">@reactufy/rebootui</Text> package bundles that sprite for{' '}
                <Text el="code">Icon</Text>; this gallery lists ids parsed at build time from a copy of
                the sprite via a Vite <Text el="code">?raw</Text> import.
            </Text>

            <Heading el="h2" className="fw-medium fs-3 mt-4 mb-3">
                Usage
            </Heading>
            <Text className="mb-3">
                Pass the symbol id as <Text el="code">name</Text>. Optional{' '}
                <Text el="code">width</Text>, <Text el="code">height</Text>,{' '}
                <Text el="code">fill</Text>, and <Text el="code">className</Text> map to the root{' '}
                <Text el="code">&lt;svg&gt;</Text>. Icons use <Text el="code">currentColor</Text>{' '}
                by default so utility classes like <Text el="code">text-primary</Text> work.
            </Text>
            <CodeSnippet>
                <CodePreview>
                    <Wrapper className="d-flex flex-wrap align-items-center gap-3">
                        <Icon name="heart" width={28} />
                        <Icon name="check" width={28} className="text-success" />
                        <Icon name="sun" width={28} className="text-warning" />
                    </Wrapper>
                </CodePreview>
                <CodeHighlighter language="tsx">{basicUsageCode}</CodeHighlighter>
            </CodeSnippet>

            <Text className="mb-3 mt-4">
                Styling with Bootstrap text utilities or an explicit <Text el="code">fill</Text>:
            </Text>
            <CodeSnippet>
                <CodePreview>
                    <Wrapper className="d-flex flex-wrap align-items-center gap-3">
                        <Icon name="sun" width={28} className="text-warning" />
                        <Icon name="moon" width={28} className="text-primary" />
                    </Wrapper>
                </CodePreview>
                <CodeHighlighter language="tsx">{styledUsageCode}</CodeHighlighter>
            </CodeSnippet>

            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                Icon set
            </Heading>
            <Text className="mb-3">
                Search by name. Click a tile to copy a ready-to-paste{' '}
                <Text el="code">Icon</Text> snippet.
            </Text>

            <Wrapper className="mb-1">
                <label htmlFor="icon-search" className="form-label visually-hidden">
                    Search icons
                </label>
                <input
                    id="icon-search"
                    type="search"
                    className="form-control form-control-lg"
                    placeholder="Search icons…"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    autoComplete="off"
                    spellCheck={false}
                />
            </Wrapper>

            <Text>
                <Text el='small' className="text-body-secondary">
                    Showing {filtered.length} of {ICON_NAMES.length} icons
                    {query.trim() ? ` matching “${query.trim()}”` : ''}.
                </Text>
            </Text>
                

            {filtered.length === 0 ? (
                <Text className="text-body-secondary">No icons match your search.</Text>
            ) : (
                <Stack
                    direction={StackDirection.Column}
                    alignItems={AlignItems.Center}
                    className="border border-black border-opacity-10 bg-white rounded-5 p-md-5 p-4"
                >
                    <div className="row row-cols-2 row-cols-sm-3 row-cols-md-4 row-cols-lg-5 row-cols-xl-6 g-3 w-100">
                        {filtered.map((name) => (
                            <div key={name} className="col d-flex">
                                <button
                                    type="button"
                                    className="btn btn-link text-decoration-none text-reset p-0 border-0 shadow-none d-flex flex-grow-1 w-100"
                                    onClick={() => copySnippet(name)}
                                    title={`Copy <Icon name="${name}" … />`}
                                >
                                    <Stack
                                        direction={StackDirection.Column}
                                        alignItems={AlignItems.Center}
                                        gap={Gap.Gap3}
                                        className="px-3 py-3 hover-scale-icon w-100 h-100 justify-content-start"
                                    >
                                        <Stack.Item className="icon bg-gray text-muted text-dark-hover p-3 rounded-4 border border-2 border-opacity-10 border-opacity-50-hover border-dark-hover tr-base flex-shrink-0">
                                            <Icon name={name} width={26} height={25} />
                                        </Stack.Item>
                                        <Stack.Item className="w-100">
                                            <Text
                                                el="p"
                                                className="mb-0 text-muted fs-xs text-center text-break font-monospace user-select-all"
                                            >
                                                {name}
                                            </Text>
                                        </Stack.Item>
                                        <Stack.Item
                                            className="w-100"
                                            style={{ minHeight: '1.25rem' }}
                                        >
                                            {copied === name ? (
                                                <Text
                                                    el="span"
                                                    className="text-success fs-xs d-block text-center"
                                                    role="status"
                                                >
                                                    Copied
                                                </Text>
                                            ) : (
                                                '\u00a0'
                                            )}
                                        </Stack.Item>
                                    </Stack>
                                </button>
                            </div>
                        ))}
                    </div>
                </Stack>
            )}
        </Container>
    );
};

export default IconsPage;